import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gestao } from './api.ts';
import { useTextos } from './i18n.ts';
import { Consulta, Feedback, Segredo, estadoTexto, useAcao } from './comum.tsx';
import { CampoChave, SituacaoChave } from './chave-colada.tsx';

type Conector = {
  id: string;
  tipo: string;
  estado: string;
  credencial_ref: string | null;
  /** so se ha chave colada e os 4 ultimos; a chave nunca vem do servidor */
  chave_salva: boolean;
  chave_final: string | null;
  config: Record<string, string>;
  ultimo_teste: string | null;
};

export const TIPOS = [
  'whatsapp',
  'instagram',
  'google-calendar',
  'youtube',
  'vimeo',
  'panda-video',
  'bunny-stream',
  'asaas',
  'stripe',
  'resend',
  'autentique',
  'google',
];
/** Nome legivel do tipo; o valor enviado continua sendo o codigo. */
function rotuloTipo(tipo: string, t: ReturnType<typeof useTextos>): string {
  const rotulos: Record<string, string> = { autentique: t.tipoAutentique, google: t.tipoGoogle };
  return rotulos[tipo] ?? tipo;
}

export function Conectores() {
  const t = useTextos();
  const acao = useAcao();
  const [edicao, setEdicao] = useState<Conector | null>(null);
  const [entrada, setEntrada] = useState<string | null>(null);
  const [segredo, setSegredo] = useState('');
  const q = useQuery({
    queryKey: ['integracoes', 'conectores'],
    queryFn: () => gestao<{
      itens: Conector[];
    }>('conectores_listar'),
  });
  const itens = q.data?.itens ?? [];
  return <div className="integracoes">
    <p>
      {t.semCredencial}
    </p>
    <p className="integracoes__nota">{t.chaveAviso}</p>
    <Feedback acao={acao} />

    <form
      key={edicao?.id ?? 'novo'}
      onSubmit={e => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        const config: Record<string, string> = {};
        for (const campo of ['conta_id', 'calendario_id', 'biblioteca_id', 'ambiente']) {
          const v = String(f.get(campo) ?? '');
          if (v)
            config[campo] = v;
        }
        void acao.executar(() => gestao(edicao ? 'conector_editar' : 'conector_criar', {
          ...(edicao ? { id: edicao.id } : {}),
          tipo: f.get('tipo'),
          credencial_ref: String(f.get('credencial_ref') ?? '') || null,
          config,
        })).then(r => {
          if (r)
            setEdicao(null);
        });
      }}
    >

      <label>
        {t.tipo}
        <select
          name="tipo"
          defaultValue={edicao?.tipo ?? 'whatsapp'}
          disabled={!!edicao}
        >
          {TIPOS.map(x => <option key={x} value={x}>
            {rotuloTipo(x, t)}
          </option>)}
        </select>
        {edicao && <input name="tipo" type="hidden" value={edicao.tipo} />}
      </label>

      <label>
        {t.referenciaOpcional}
        <input
          name="credencial_ref"
          placeholder="op:// ou env://ASAAS_API_KEY, env://AUTENTIQUE_API_TOKEN, env://GOOGLE_REFRESH_TOKEN"
          defaultValue={edicao?.credencial_ref ?? ''}
        />
      </label>

      <label>
        {t.conta}
        <input name="conta_id" defaultValue={edicao?.config.conta_id ?? ''} />
      </label>

      <label>
        {t.calendario}
        <input
          name="calendario_id"
          defaultValue={edicao?.config.calendario_id ?? ''}
        />
      </label>

      <label>
        {t.biblioteca}
        <input
          name="biblioteca_id"
          defaultValue={edicao?.config.biblioteca_id ?? ''}
        />
      </label>

      <label>
        {t.ambiente}
        <select
          name="ambiente"
          defaultValue={edicao?.config.ambiente ?? 'producao'}
        >
          <option value="producao">
            {t.producao}
          </option>
          <option value="sandbox">
            {t.sandbox}
          </option>
        </select>
      </label>

      <button disabled={acao.ocupado}>
        {edicao ? t.salvar : t.criar}
      </button>
      {edicao
        && <button type="button" onClick={() => setEdicao(null)}>
        {t.cancelar}
      </button>}
    </form>
    {edicao && <CampoChave key={`${edicao.id}-${edicao.chave_final ?? ''}`}
      rota="conector_chave_salvar" id={edicao.id}
      salva={itens.find(c => c.id === edicao.id)?.chave_salva ?? edicao.chave_salva}
      final={itens.find(c => c.id === edicao.id)?.chave_final ?? edicao.chave_final} />}

    <Consulta consulta={q} vazio={!itens.length}>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.tipo}
              </th>
              <th>
                {t.estado}
              </th>
              <th>
                {t.referencia}
              </th>
              <th>
                {t.chaveColada}
              </th>
              <th>
                {t.detalhes}
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map(c => <tr key={c.id}>
              <td>
                {rotuloTipo(c.tipo, t)}
              </td>
              <td>
                {estadoTexto(c.estado, t)}
              </td>
              <td>
                {c.credencial_ref ?? '-'}
              </td>
              <td>
                <SituacaoChave salva={c.chave_salva} final={c.chave_final} />
              </td>
              <td>

                <button onClick={() => setEdicao(c)}>
                  {t.editar}
                </button>
                <button
                  disabled={acao.ocupado}
                  onClick={() => void acao.executar(
                    () => gestao(
                      'conector_testar',
                      {
                        id: c.id,
                      },
                    ),
                  )}
                >
                  {t.testar}
                </button>
                <button
                  disabled={acao.ocupado}
                  onClick={() => void acao.executar(
                    () => gestao(
                      'conector_reconectar',
                      {
                        id: c.id,
                      },
                    ),
                  )}
                >
                  {t.reconectar}
                </button>
                <button
                  disabled={acao.ocupado}
                  onClick={() => void acao.executar(
                    () => gestao(
                      'conector_excluir',
                      {
                        id: c.id,
                      },
                    ),
                  )}
                >
                  {t.excluir}
                </button>
                {!['youtube', 'vimeo', 'panda-video', 'bunny-stream', 'google'].includes(c.tipo)
                  && <button onClick={() => setEntrada(c.id)}>
                  {t.configurarEntrada}
                </button>}

              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </Consulta>

    {entrada
      && <form
      onSubmit={e => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        void acao.executar(() => gestao<{
          url: string;
        }>('entrada_configurar', {
          conector_id: entrada,
          segredo_ref: String(f.get('segredo_ref') ?? '') || null,
          verificacao_ref: String(f.get('verificacao_ref') ?? '') || null,
        })).then(r => {
          if (r) {
            setSegredo(r.url);
            setEntrada(null);
          }
        });
      }}
    >
      <label>
        {t.segredoEntrada}
        <input name="segredo_ref" placeholder="op://"
          required={itens.find(c => c.id === entrada)?.tipo !== 'autentique'} />
      </label>
      {itens.find(c => c.id === entrada)?.tipo === 'autentique'
        && <p className="integracoes__nota">{t.segredoOpcionalAutentique}</p>}
      <label>
        {t.verificacao}
        <input name="verificacao_ref" placeholder="op://" />
      </label>
      <button disabled={acao.ocupado}>
        {t.configurarEntrada}
      </button>
      <button type="button" onClick={() => setEntrada(null)}>
        {t.cancelar}
      </button>
    </form>}

    {segredo && <Segredo valor={segredo} fechar={() => setSegredo('')} />}
  </div>;
}
